"use client";

import { useState, useEffect } from "react";
import HeroGreeting from "./HeroGreeting";
import SmartTaskCard from "./SmartTaskCard";

// 業主反饋:「看完就不想繼續看」。但 task 是真業務待辦,不能永久關。
// 折衷:點 × 隱藏 24 小時,過期再出現,確保不會漏訂單。
const DISMISS_KEY = "smart_inbox_dismissed_v1";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function loadDismissed() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    const now = Date.now();
    const fresh = {};
    for (const [id, ts] of Object.entries(obj)) {
      if (typeof ts === "number" && now - ts < DISMISS_TTL_MS) fresh[id] = ts;
    }
    return fresh;
  } catch {
    return {};
  }
}

function persistDismissed(map) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {
    /* localStorage 配額 / 隱身模式失敗 → 失敗無聲:當次仍可隱藏,只是 refresh 後回來 */
  }
}

// Pure helpers exported for unit testing without a DOM renderer.

export function computeActionableCount(tasks) {
  if (!Array.isArray(tasks)) return 0;
  return tasks
    .filter((t) => t && t.severity !== "clear")
    .reduce((n, t) => n + (typeof t.count === "number" ? t.count : 0), 0);
}

export function deriveRenderMode({ tasks, error } = {}) {
  if (error) return "error";
  if (tasks === null || tasks === undefined) return "loading";
  return "list";
}

export default function SmartInbox({ now, tasks, weeklyRevenue, error }) {
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    setDismissed(loadDismissed());
  }, []);

  function handleDismiss(taskId) {
    setDismissed((prev) => {
      const next = { ...prev, [taskId]: Date.now() };
      persistDismissed(next);
      return next;
    });
  }

  const visibleTasks = Array.isArray(tasks)
    ? tasks.filter((t) => !dismissed[t.id])
    : tasks;
  const actionableCount = computeActionableCount(visibleTasks);
  const mode = deriveRenderMode({ tasks: visibleTasks, error });

  return (
    <section className="admin-smart-inbox" aria-labelledby="smart-inbox-title">
      <HeroGreeting
        now={now}
        count={actionableCount}
        weeklyRevenue={weeklyRevenue ?? 0}
      />

      {mode === "loading" && (
        <div
          className="admin-smart-inbox__skeleton"
          data-testid="smart-inbox-skeleton"
          role="status"
          aria-label="載入中"
        >
          <div className="admin-smart-inbox__skeleton-row" />
          <div className="admin-smart-inbox__skeleton-row" />
          <div className="admin-smart-inbox__skeleton-row" />
        </div>
      )}

      {mode === "error" && (
        <div className="admin-smart-inbox__error" role="alert">
          {error}
        </div>
      )}

      {mode === "list" && (
        <ul className="admin-smart-inbox__list">
          {visibleTasks.map((task) => (
            <li key={task.id} className="admin-smart-inbox__task-row">
              <SmartTaskCard task={task} />
              {task.severity !== "clear" && (
                <button
                  type="button"
                  onClick={() => handleDismiss(task.id)}
                  className="admin-smart-inbox__dismiss"
                  aria-label="先收起這則提醒(24 小時後會再出現,避免漏)"
                  title="先收起來 — 24 小時後會再出現"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
